import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";

const Crumb = () => <div className="text-[11px] uppercase tracking-wide text-wmuted mb-1">Sales / Pre-Order / Analisis Produk & Varian</div>;
const PAGE = 20;

export default function PreOrderAnalysis() {
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [q, setQ] = useState(params.get("q") || "");
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true); setError(false);
    api.get("/preorders/virtual-warehouse", { params: { batch_status: "Open" } })
      .then((r) => setData(r.data))
      .catch((e) => { setError(true); toast.error(formatApiError(e.response?.data?.detail)); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { setPage(1); }, [q]);

  const rows = useMemo(() => {
    let arr = data?.items || [];
    if (q.trim()) { const ql = q.toLowerCase(); arr = arr.filter((r) => (r.product_name + r.color + r.size).toLowerCase().includes(ql)); }
    return arr;
  }, [data, q]);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const view = rows.slice((page - 1) * PAGE, page * PAGE);

  return (
    <div className="space-y-4" data-testid="preorder-analysis-page">
      <div><Crumb />
        <PageHeader title="Analisis Produk & Varian" subtitle="Bandingkan kuota per varian & batch dari data existing. Kolom mengikuti Planned/Reserved/Sold/Remaining existing." />
      </div>
      {loading ? (
        <Panel className="p-10 text-center text-wmuted" data-testid="po-an-loading">Memuat…</Panel>
      ) : error ? (
        <Panel className="p-10 text-center" data-testid="po-an-error"><div className="text-status-reject mb-2">Gagal memuat data.</div><Button variant="outline" onClick={load} className="border-cocoa text-cocoa">Coba lagi</Button></Panel>
      ) : (
        <>
          <Panel className="p-0 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-taupe/15">
              <h3 className="font-semibold text-cocoa">Kuota per Varian (batch Open)</h3>
              <div className="relative ml-auto w-64"><Search className="w-4 h-4 absolute left-2 top-2.5 text-wmuted" /><Input className="pl-8 bg-white" placeholder="Cari produk / warna / ukuran…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="po-an-search" /></div>
            </div>
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Produk</TableHead><TableHead>Warna/Size</TableHead><TableHead>Batch</TableHead><TableHead className="text-right">Rencana</TableHead><TableHead className="text-right">Di-Keep</TableHead><TableHead className="text-right">Terjual</TableHead><TableHead className="text-right">Barang Datang</TableHead><TableHead className="text-right">Sisa</TableHead></TableRow></TableHeader>
              <TableBody>
                {view.map((r) => (
                  <TableRow key={r.variant_id} data-testid={`po-an-row-${r.variant_id}`}>
                    <TableCell className="font-medium text-cocoa">{r.product_name}</TableCell>
                    <TableCell className="text-sm text-wmuted">{r.color}/{r.size}</TableCell>
                    <TableCell className="text-xs text-wmuted">{(r.batches || []).map((b) => b.batch_name).join(", ") || "-"}</TableCell>
                    <TableCell className="text-right">{r.planned}</TableCell>
                    <TableCell className="text-right text-amber-600">{r.reserved}</TableCell>
                    <TableCell className="text-right">{r.sold}</TableCell>
                    <TableCell className="text-right text-emerald-600">{r.arrived}</TableCell>
                    <TableCell className="text-right font-semibold">{r.remaining}</TableCell>
                  </TableRow>
                ))}
                {!view.length && <TableRow><TableCell colSpan={8} className="text-center text-wmuted py-10">Tidak ada varian sesuai filter.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Panel>
          {pages > 1 && (
            <div className="flex items-center justify-between text-sm text-wmuted">
              <span>Menampilkan {view.length} dari {rows.length} varian</span>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} className="border-taupe text-cocoa h-8" data-testid="po-an-prev"><ChevronLeft className="w-4 h-4" /></Button>
                <span className="px-2">{page}/{pages}</span>
                <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} className="border-taupe text-cocoa h-8" data-testid="po-an-next"><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
          <div className="flex items-start gap-2 text-xs text-wmuted bg-ivory rounded-lg p-3"><AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />Asal permintaan kanal (Shopee/TikTok/Website) tidak disimpulkan dari nama customer atau batch. Angka murni mengikuti kuota batch existing.</div>
        </>
      )}
    </div>
  );
}
